import * as React from 'react';
import { cn, getTestId } from '../../utils';
import { Icon } from '../icons/Icon';

type BaseFilterOptionAttributes = Pick<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'id' | 'className' | 'disabled'
>;

export interface FilterOptionProps extends BaseFilterOptionAttributes {
  /** Display label */
  label: string;
  /** Whether this option is currently selected */
  selected?: boolean;
  /** Optional color dot displayed before the label (Tailwind bg class, e.g. `"bg-success-text"`) */
  dotColor?: string;
  /** Click handler */
  onClick?: () => void;
  /** Test ID for automated testing */
  testId?: string;
}

/**
 * A selectable list-item button used inside filter popovers and dropdowns.
 *
 * Displays a label with an optional leading color dot and a trailing
 * check icon when `selected` is true.
 *
 * @example
 * ```tsx
 * <FilterOption
 *   label="Active"
 *   selected={filters.status === 'ACTIVE'}
 *   dotColor="bg-success-text"
 *   onClick={() => setFilter('status', 'ACTIVE')}
 * />
 * ```
 */
export const FilterOption = React.forwardRef<HTMLButtonElement, FilterOptionProps>(
  (
    { label, selected = false, dotColor, onClick, disabled, className, testId, id, ...props },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type='button'
        role='option'
        aria-selected={selected}
        aria-disabled={disabled}
        data-testid={getTestId({ testId, id })}
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        className={cn(
          'w-full flex items-center justify-between px-sm py-sm rounded-md text-sm transition-colors cursor-pointer',
          'text-foreground-base hover:bg-background-muted',
          'focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary-base',
          disabled && 'opacity-50 cursor-not-allowed hover:bg-transparent',
          className
        )}
        {...props}
      >
        <span className='flex items-center gap-sm'>
          {dotColor && (
            <span className={cn('w-2 h-2 rounded-full shrink-0', dotColor)} aria-hidden='true' />
          )}
          {label}
        </span>
        {selected && (
          <Icon
            name='check'
            className='w-3.5! h-3.5! text-primary-base shrink-0'
            aria-hidden='true'
          />
        )}
      </button>
    );
  }
);

FilterOption.displayName = 'FilterOption';
